import { functions, firestore } from "@/plugins/firebase"
import { httpsCallable } from "firebase/functions"
import { collection, query, orderBy, startAt, onSnapshot } from "firebase/firestore"

const PREISTYPEN = ["DFC_Live", "DFC_Schlusskurs", "HFC_Live", "HFC_Schlusskurs"]
const SCHLUSSKURS_TYPEN = ["DFC_Schlusskurs", "HFC_Schlusskurs"]

function parseProduktname(produktname) {
    const prefix = produktname.substring(0, 2)
    const basePeak = produktname.substring(2, 3)
    const laufzeit = produktname.substring(3, 4)
    return {
        energietyp: prefix === "DE" ? "Strom" : "Gas",
        produkttyp: basePeak === "B" ? "Base" : "Peak",
        laufzeit: laufzeit === "Y" ? "Jahr" : laufzeit === "Q" ? "Quartal" : "Monat",
    }
}

function mergePreise(rohPreise) {
    const preisMap = {}
    for (const p of rohPreise) {
        const key = p.timestamp.toString()
        if (!preisMap[key] || p.quelle === "S") {
            preisMap[key] = p
        }
    }
    return Object.values(preisMap).sort((a, b) => a.timestamp - b.timestamp)
}

export default {
    namespaced: true,
    state: {
        produkte: {},          // { [produktname]: { preise: [], energietyp, produkttyp, laufzeit } }
        produktListe: [],      // sortierte Liste der Produktnamen
        rohPreise: {},         // { [produktname]: { [preistyp]: [ {timestamp, price, quelle} ] } }
        listeners: [],         // aktive onSnapshot-Unsubscribe-Funktionen
        loading: false,
        loadingProdukte: {},   // { [produktname]: true/false }
        error: null,
        schwellwerte: {
            Strom: {
                Base: { stufen: [0.1, 0.3, 0.5, 1, 2] },
                Peak: { stufen: [0.1, 0.3, 0.5, 1, 2] },
            },
            Gas: {
                Base: { stufen: [0.1, 0.3, 0.5, 1, 2] },
                Peak: { stufen: [0.1, 0.3, 0.5, 1, 2] },
            },
        },
        farbkonfiguration: {
            steigend: [
                "#FFCDD2",
                "#EF9A9A",
                "#E57373",
                "#EF5350",
                "#D32F2F",
            ],
            fallend: [
                "#C8E6C9",
                "#A5D6A7",
                "#81C784",
                "#66BB6A",
                "#388E3C",
            ],
            neutral: "#F5F5F5",
            fehlend: "#BDBDBD",
        },
        deltaKonfiguration: {
            stufen: [
                { bisMinuten: 5,   farbe: "#FFF9C4", label: "< 5 Min" },
                { bisMinuten: 15,  farbe: "#FFF176", label: "< 15 Min" },
                { bisMinuten: 30,  farbe: "#FFD54F", label: "< 30 Min" },
                { bisMinuten: 60,  farbe: "#B3E5FC", label: "< 1 Std" },
                { bisMinuten: 120, farbe: "#64B5F6", label: "< 2 Std" },
            ],
            fallback: "#1E88E5",
        },
        filter: {
            energietyp: "alle",
            produkttyp: "alle",
            laufzeiten: ["Y"],  // Standard: nur Jahresprodukte. "Q" kann hinzugeschaltet werden
            tage: 3,
        },
    },
    getters: {
        getHeatmapData(state) {
            const rows = []
            for (const produktname of state.produktListe) {
                const prod = state.produkte[produktname]
                if (!prod) continue

                if (state.filter.energietyp !== "alle" && prod.energietyp !== state.filter.energietyp) continue
                if (state.filter.produkttyp !== "alle" && prod.produkttyp !== state.filter.produkttyp) continue

                const preisMap = {}
                const sortiertePreise = [...prod.preise].sort((a, b) => a.timestamp - b.timestamp)

                for (let i = 0; i < sortiertePreise.length; i++) {
                    const p = sortiertePreise[i]
                    const slotKey = p.timestamp.toString()
                    let abweichung = null
                    if (i > 0) {
                        const vorPreis = sortiertePreise[i - 1].price
                        if (vorPreis !== 0) {
                            abweichung = ((p.price - vorPreis) / Math.abs(vorPreis)) * 100
                        }
                    }
                    preisMap[slotKey] = {
                        timestamp: p.timestamp,
                        price: p.price,
                        abweichung: abweichung,
                        quelle: p.quelle || null,
                        created: p.created || null,
                        importDelta: (p.created && p.timestamp) ? (p.created - p.timestamp) : null,
                    }
                }

                rows.push({
                    produktname,
                    energietyp: prod.energietyp,
                    produkttyp: prod.produkttyp,
                    laufzeit: prod.laufzeit,
                    preisMap,
                    letzterPreis: sortiertePreise.length > 0 ? sortiertePreise[sortiertePreise.length - 1] : null,
                    anzahlPreise: sortiertePreise.length,
                })
            }
            return rows
        },

        // Neueste Zeitpunkte zuerst (absteigend)
        getAlleZeitpunkte(state) {
            const tsSet = new Set()
            for (const produktname of state.produktListe) {
                const prod = state.produkte[produktname]
                if (!prod) continue
                for (const p of prod.preise) {
                    tsSet.add(p.timestamp)
                }
            }
            const sorted = Array.from(tsSet).sort((a, b) => b - a) // absteigend
            let prevDatum = null
            return sorted.map((ts) => {
                const d = new Date(ts)
                const datum =
                    d.getFullYear() + "-" +
                    String(d.getMonth() + 1).padStart(2, "0") + "-" +
                    String(d.getDate()).padStart(2, "0")
                const istTageswechsel = prevDatum !== null && datum !== prevDatum
                prevDatum = datum
                return {
                    key: ts.toString(),
                    timestamp: ts,
                    datum,
                    uhrzeit: String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0"),
                    wochentag: d.getDay(),
                    istWochenende: d.getDay() === 0 || d.getDay() === 6,
                    istTageswechsel,
                }
            })
        },

        getTagesGruppen(state, getters) {
            const zeitpunkte = getters.getAlleZeitpunkte
            const gruppen = []
            let currentDatum = null
            let currentGruppe = null
            for (const zp of zeitpunkte) {
                if (zp.datum !== currentDatum) {
                    currentDatum = zp.datum
                    currentGruppe = { datum: zp.datum, istWochenende: zp.istWochenende, spalten: [] }
                    gruppen.push(currentGruppe)
                }
                currentGruppe.spalten.push(zp)
            }
            return gruppen
        },

        getFarbUndPfeil: (state) => (abweichung, energietyp, produkttyp) => {
            if (abweichung === null || abweichung === undefined) {
                return { farbe: state.farbkonfiguration.neutral, pfeil: "", textFarbe: "#757575" }
            }
            const typ = energietyp || "Strom"
            const pt = produkttyp || "Base"
            const stufen =
                state.schwellwerte[typ] && state.schwellwerte[typ][pt]
                    ? state.schwellwerte[typ][pt].stufen
                    : [0.1, 0.3, 0.5, 1, 2]
            const abs = Math.abs(abweichung)
            let stufe = 0
            for (let i = 0; i < stufen.length; i++) {
                if (abs >= stufen[i]) stufe = i
            }
            const richtung = abweichung > 0 ? "steigend" : "fallend"
            const farben = state.farbkonfiguration[richtung]
            if (abs < stufen[0]) {
                return { farbe: state.farbkonfiguration.neutral, pfeil: "", textFarbe: "#757575" }
            }
            return {
                farbe: farben[stufe],
                pfeil: abweichung > 0 ? "mdi-arrow-up-bold" : "mdi-arrow-down-bold",
                textFarbe: abweichung > 0 ? "#B71C1C" : "#1B5E20",
            }
        },

        geladeneProdukte(state) {
            return state.produktListe.filter((p) => state.produkte[p])
        },
    },
    mutations: {
        SET_PRODUKT_LISTE(state, liste) {
            state.produktListe = liste
        },
        SET_LOADING(state, val) {
            state.loading = val
        },
        SET_ERROR(state, val) {
            state.error = val
        },
        SET_FILTER(state, filter) {
            state.filter = { ...state.filter, ...filter }
        },
        SET_SCHWELLWERTE(state, { energietyp, produkttyp, stufen }) {
            if (state.schwellwerte[energietyp] && state.schwellwerte[energietyp][produkttyp]) {
                state.schwellwerte[energietyp][produkttyp].stufen = stufen
            }
        },
        SET_FARBEN(state, { richtung, farben }) {
            if (state.farbkonfiguration[richtung]) {
                state.farbkonfiguration[richtung] = farben
            }
        },
        SET_DELTA_CONFIG(state, { index, bisMinuten, farbe }) {
            if (index !== undefined && state.deltaKonfiguration.stufen[index]) {
                if (bisMinuten !== undefined) {
                    state.deltaKonfiguration.stufen[index].bisMinuten = bisMinuten
                }
                if (farbe !== undefined) {
                    state.deltaKonfiguration.stufen[index].farbe = farbe
                }
            }
        },
        SET_DELTA_FALLBACK(state, farbe) {
            state.deltaKonfiguration.fallback = farbe
        },
        SET_ROH_PREISE(state, { produktname, preistyp, preise }) {
            if (!state.rohPreise[produktname]) {
                state.rohPreise = { ...state.rohPreise, [produktname]: {} }
            }
            const roh = { ...state.rohPreise[produktname], [preistyp]: preise }
            state.rohPreise = { ...state.rohPreise, [produktname]: roh }

            // Merge aller Preistypen für dieses Produkt
            const allePT = []
            for (const pt of PREISTYPEN) {
                if (roh[pt]) allePT.push(...roh[pt])
            }
            const merged = mergePreise(allePT)
            const info = parseProduktname(produktname)

            state.produkte = {
                ...state.produkte,
                [produktname]: {
                    preise: merged,
                    ...info,
                    letzterPreis: merged.length > 0 ? merged[merged.length - 1] : null,
                },
            }
        },
        SET_LOADING_PRODUKT(state, { produktname, loading }) {
            state.loadingProdukte = { ...state.loadingProdukte, [produktname]: loading }
        },
        ADD_LISTENER(state, unsubscribe) {
            state.listeners.push(unsubscribe)
        },
        CLEAR_ALL(state) {
            // Alle Listener stoppen
            for (const unsub of state.listeners) {
                try { unsub() } catch (e) { /* ignore */ }
            }
            state.listeners = []
            state.produkte = {}
            state.produktListe = []
            state.rohPreise = {}
            state.loadingProdukte = {}
        },
    },
    actions: {
        /**
         * Stoppt alle aktiven Listener.
         */
        stopListeners({ commit }) {
            commit("CLEAR_ALL")
        },

        /**
         * Hauptaktion: Produktliste laden, dann pro Produkt onSnapshot-Listener starten.
         */
        async startMonitoring({ commit, dispatch, state }) {
            commit("CLEAR_ALL")
            commit("SET_LOADING", true)
            commit("SET_ERROR", null)

            try {
                // 1. Produktliste via Cloud Function holen
                const getProduktListe = httpsCallable(functions, "getPreisMonitorProduktListe")
                const result = await getProduktListe({
                    energietyp: state.filter.energietyp,
                    laufzeiten: state.filter.laufzeiten,
                })
                const produktListe = result.data.produkte || []
                commit("SET_PRODUKT_LISTE", produktListe)

                console.log("Preismonitor: " + produktListe.length + " Produkte, starte Listener...")

                // 2. Pro Produkt Firestore-Listener starten
                const now = new Date()
                const tageMs = state.filter.tage * 24 * 60 * 60 * 1000
                const vonTimestamp = now.getTime() - tageMs

                for (const produktname of produktListe) {
                    commit("SET_LOADING_PRODUKT", { produktname, loading: true })
                    dispatch("subscribeProdukt", { produktname, vonTimestamp })
                }
            } catch (err) {
                console.error("Fehler beim Laden der Produktliste:", err)
                commit("SET_ERROR", err.message || "Unbekannter Fehler")
            } finally {
                commit("SET_LOADING", false)
            }
        },

        /**
         * Registriert onSnapshot-Listener für alle 4 Preistypen eines Produkts.
         */
        subscribeProdukt({ commit }, { produktname, vonTimestamp }) {
            const vonStr = vonTimestamp.toString()

            for (const pt of PREISTYPEN) {
                const colRef = collection(firestore, "preise", pt, produktname)
                const q = query(colRef, orderBy("__name__"), startAt(vonStr))
                const isSchlusskurs = SCHLUSSKURS_TYPEN.includes(pt)

                const unsubscribe = onSnapshot(q, (snapshot) => {
                    const preise = []
                    snapshot.forEach((doc) => {
                        const ts = parseInt(doc.id)
                        if (isNaN(ts)) return
                        const docData = doc.data()
                        const quelle = isSchlusskurs ? "S" : "L"

                        let displayTs = ts
                        if (isSchlusskurs) {
                            const d = new Date(ts)
                            d.setHours(23, 45, 0, 0)
                            displayTs = d.getTime()
                        }

                        preise.push({
                            timestamp: displayTs,
                            price: parseFloat(docData.price),
                            quelle,
                            created: docData.created ? (docData.created.seconds * 1000) : null,
                        })
                    })

                    commit("SET_ROH_PREISE", { produktname, preistyp: pt, preise })
                    commit("SET_LOADING_PRODUKT", { produktname, loading: false })
                }, (err) => {
                    console.error(`Listener-Fehler für ${produktname}/${pt}:`, err)
                })

                commit("ADD_LISTENER", unsubscribe)
            }
        },
    },
}
